import type * as maplibregl from 'maplibre-gl'
import { useEffect, useRef, useState } from 'react'

/**
 * How long the map has to be quiet (no canvas resize, no camera move) before
 * held-back items are shown. Comfortably longer than MapLibre's 50ms resize
 * throttle, so a CSS width transition reads as one continuous busy spell.
 */
export const MAP_SETTLE_DELAY_MS = 250
/** Show held-back items after this long, even if the map never goes quiet. */
export const MAP_SETTLE_MAX_WAIT_MS = 2500

const NOTHING: never[] = []

/**
 * Holds `items` back while the map is resizing or its camera is moving, and
 * hands them over once both have been quiet for {@link MAP_SETTLE_DELAY_MS}.
 *
 * Meant for layers that are expensive to build. Opening the route editor
 * slides the map column over 300ms and flies the camera to the route, and
 * building hundreds of annotation markers (each with a popup, a portal and a
 * pop-in animation) in those same frames made the whole transition stutter.
 *
 * The first set of items always waits for a quiet spell, even on a still map:
 * whatever brings the items (opening the editor) usually also starts the
 * resize and the camera fly, just a frame or two later. After that, a new set
 * replaces the shown one straight away unless the map is busy right then, so
 * a filter change on a still map feels instant. An empty list always goes
 * through straight away, so leaving the editor never leaves markers behind.
 *
 * Nothing is shown before there is a map; the wait starts when it arrives.
 */
export const useItemsAfterMapSettles = <T>(
  map: maplibregl.Map | null,
  items: T[],
): T[] => {
  const [shown, setShown] = useState<T[]>(NOTHING)
  const shownRef = useRef<T[]>(NOTHING)
  const lastResizeAtRef = useRef(Number.NEGATIVE_INFINITY)
  // When the items now being held back first started waiting, so a later
  // update of those items does not restart the maximum wait.
  const pendingSinceRef = useRef<number | null>(null)

  // Remember the last canvas resize, so a replacement arriving mid-transition
  // knows to wait as well.
  useEffect(() => {
    if (!map) return
    const onResize = () => {
      lastResizeAtRef.current = Date.now()
    }
    map.on('resize', onResize)
    return () => {
      map.off('resize', onResize)
    }
  }, [map])

  useEffect(() => {
    if (!map) return

    const show = () => {
      pendingSinceRef.current = null
      if (shownRef.current === items) return
      shownRef.current = items
      setShown(items)
    }

    if (items === shownRef.current) {
      pendingSinceRef.current = null
      return
    }
    if (items.length === 0 && shownRef.current.length === 0) {
      pendingSinceRef.current = null
      return
    }
    const isBusy =
      map.isMoving() ||
      Date.now() - lastResizeAtRef.current < MAP_SETTLE_DELAY_MS
    if (items.length === 0 || (shownRef.current.length > 0 && !isBusy)) {
      show()
      return
    }

    pendingSinceRef.current ??= Date.now()
    const maxWaitLeft =
      pendingSinceRef.current + MAP_SETTLE_MAX_WAIT_MS - Date.now()
    let settleTimer: ReturnType<typeof setTimeout> | null = null
    // Every resize and every finished camera move restarts the quiet spell. A
    // timer that runs out while the camera is still moving does nothing; the
    // move's own `moveend` starts the next one.
    const settle = () => {
      if (settleTimer) clearTimeout(settleTimer)
      settleTimer = setTimeout(() => {
        settleTimer = null
        if (!map.isMoving()) show()
      }, MAP_SETTLE_DELAY_MS)
    }
    const maxWaitTimer = setTimeout(show, Math.max(0, maxWaitLeft))
    map.on('resize', settle)
    map.on('moveend', settle)
    settle()

    return () => {
      if (settleTimer) clearTimeout(settleTimer)
      clearTimeout(maxWaitTimer)
      map.off('resize', settle)
      map.off('moveend', settle)
    }
  }, [map, items])

  return shown
}
